import AutorenewIcon from '@mui/icons-material/Autorenew'
import { IconButton, InputAdornment, TextField, Tooltip } from '@mui/material'
import { MRT_RowData } from 'material-react-table'
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'

import { useAppContext } from '@/hooks'
import { useAdjustTableNameUsingPostMutation } from '@/rtkApi'
import { logError } from '@/utils/errorHandling'

import { textCellKeyDownHandler } from '../helpers'
import { TextFieldSx } from '../styles'
import { type MrtCellProps } from '../types'
import { getInitCellValue } from '../utils'
import { CellTooltip } from './CellTooltip'

const MIN_TOOLTIP_LENGTH = 13

interface CodeCellProps<TData extends MRT_RowData> extends MrtCellProps<TData> {
	autoGenerate?: boolean,
	isUpperCase?: boolean,
	nameAccessor?: string,
	showGenerateButton?: boolean
}

export const CodeCell = <TData extends MRT_RowData>({
	cell,
	row,
	column,
	updateCellValue,
	customValue,
	isDisabled = false,
	isReadOnly = false,
	showGenerateButton = false,
	isUpperCase = true,
	nameAccessor = 'name',
	autoGenerate = false,
}: CodeCellProps<TData>) => {
	const { t } = useAppContext()
	const [value, setValue] = useState<string>('')
	const [isGenerating, setIsGenerating] = useState(false)
	const [adjustTableNameMutation] = useAdjustTableNameUsingPostMutation()

	const cellValue = cell?.getValue()
	const nameValue = row?.original?.[nameAccessor] as string | undefined

	const initialValueRef = useRef<string>('')
	const hasValueChangedRef = useRef<boolean>(false)
	const hasCommittedRef = useRef<boolean>(false)
	const previousNameRef = useRef<string | undefined>(undefined)

	useEffect(() => {
		const initialValue = getInitCellValue(customValue, cellValue)
		setValue(initialValue)
		initialValueRef.current = initialValue
		hasValueChangedRef.current = false
		hasCommittedRef.current = false
	}, [cellValue, customValue])

	const commitIfChanged = useCallback(
		(nextValue: string) => {
			if (hasCommittedRef.current || !hasValueChangedRef.current) return

			if (updateCellValue && row?.index !== undefined && column?.id) {
				updateCellValue(row.index, column.id, nextValue)
				hasCommittedRef.current = true
				hasValueChangedRef.current = false
				initialValueRef.current = nextValue
			}
		},
		[updateCellValue, row?.index, column?.id],
	)

	const handleBlur = useCallback(
		(event: React.FocusEvent<HTMLInputElement>) => {
			commitIfChanged(event.target.value)
		},
		[commitIfChanged],
	)

	const handleChange = useCallback(
		(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const nextValue = event.target.value
			setValue(nextValue)
			hasValueChangedRef.current = nextValue !== initialValueRef.current
		},
		[],
	)

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			textCellKeyDownHandler(event)
			if (event.key === 'Tab') {
				commitIfChanged((event.target as HTMLInputElement).value)
			}
		},
		[commitIfChanged],
	)

	const handleGenerateCode = useCallback(async () => {
		if (!nameValue) return

		setIsGenerating(true)
		try {
			const response = await adjustTableNameMutation({
				utilsNameRequestDto: { name: nameValue },
			}).unwrap()

			const generatedCode = isUpperCase
				? response.result
				: response.result.toLowerCase()

			setValue(generatedCode)
			initialValueRef.current = generatedCode

			if (updateCellValue && row?.index !== undefined && column?.id) {
				updateCellValue(row.index, column.id, generatedCode)
			}
		} catch (error) {
			logError(error, { operation: 'CodeCell.handleGenerateCode' })
		} finally {
			setIsGenerating(false)
		}
	}, [
		nameValue,
		adjustTableNameMutation,
		isUpperCase,
		updateCellValue,
		row?.index,
		column?.id,
	])

	// Auto-generate code when name changes (if autoGenerate is enabled)
	useEffect(() => {
		if (!autoGenerate || !nameValue) return

		// Only generate if name actually changed (not on initial render)
		const nameChanged = previousNameRef.current !== undefined && previousNameRef.current !== nameValue
		previousNameRef.current = nameValue

		if (!nameChanged || isGenerating) return

		// Call mutation directly to avoid stale closure issues
		const generateCode = async () => {
			setIsGenerating(true)
			try {
				const response = await adjustTableNameMutation({
					utilsNameRequestDto: { name: nameValue },
				}).unwrap()

				const generatedCode = isUpperCase
					? response.result
					: response.result.toLowerCase()

				setValue(generatedCode)
				initialValueRef.current = generatedCode

				if (updateCellValue && row?.index !== undefined && column?.id) {
					updateCellValue(row.index, column.id, generatedCode)
				}
			} catch (error) {
				logError(error, { operation: 'CodeCell.autoGenerate' })
			} finally {
				setIsGenerating(false)
			}
		}

		generateCode()
	}, [autoGenerate, nameValue, isUpperCase, adjustTableNameMutation, updateCellValue, row?.index, column?.id, isGenerating])

	const showTooltip = value.length > MIN_TOOLTIP_LENGTH
	const canGenerate = showGenerateButton && !isDisabled && !isReadOnly

	return (
		<CellTooltip
			title={showTooltip ? value : undefined}
			isEditMode={!isDisabled && !isReadOnly}
		>
			<TextField
				disabled={isDisabled}
				fullWidth
				onBlur={handleBlur}
				onChange={handleChange}
				slotProps={{
					input: {
						readOnly: isReadOnly,
						onKeyDown: handleKeyDown,
						disableUnderline: isDisabled,
						endAdornment: canGenerate ? (
							<InputAdornment position="end">
								<Tooltip title={t('GENERATE_CODE_FROM_NAME')}>
									<span>
										<IconButton
											size="small"
											onClick={handleGenerateCode}
											disabled={!nameValue || isGenerating}
											data-testid={`table-code-button-generate-${row.index}`}
										>
											<AutorenewIcon
												fontSize="small"
												className={isGenerating ? 'animate-spin' : ''}
											/>
										</IconButton>
									</span>
								</Tooltip>
							</InputAdornment>
						) : undefined,
					},
					htmlInput: {
						'data-testid': `table-code-${row.index}-${column.id}`,
					},
				}}
				sx={TextFieldSx(isReadOnly, isDisabled)}
				type="text"
				value={value}
				variant="standard"
			/>
		</CellTooltip>
	)
}

